import { useEffect } from 'react';

// T270(2) / T161 — the triage keyboard map, lifted out of DashboardPage.
//
// It was ~80 lines of branch inside a 2700-line component, which is most of why
// it was hard to see that the whole map is one concern: move, act on the active
// row, or open something. Nothing about the behaviour changes — the effect
// still re-binds on every availableRecs / activeRecIndex change so the closure
// captures fresh values rather than reaching through refs.
//
// Everything it needs is a parameter now, which is the point: the shortcuts
// become testable without mounting the dashboard.
export function useTriageKeyboard({
  enabled = true,
  // Whether the help sheet is currently up. Needed so Escape can close it even
  // though its being up is what disarms everything else here.
  showKbHelpOpen = false,
  availableRecs,
  activeRecIndex,
  setActiveRecIndex,
  updateRecStatus,
  requestDismiss,
  explainRec,
  openCoverLetter,
  openTailoredCv,
  setDrawerOpen,
  setShowKbHelp,
  dismissPresets,
}) {
  // T085 — Keyboard shortcuts on available rec rows. Vim-style: j/k or
  // ↓/↑ for nav, s/a for shortlist/apply, d to dismiss (1–6 with a preset
  // reason, shift+D for a written one), e to explain, c/t to open the
  // cover-letter / tailored-CV modals, o or Enter to open the drawer,
  // ? for help. Ignore when typing in an input.
  //
  // T220 — `d` used to open a modal. It is the most frequent action in triage
  // and it was the most expensive one; the modifier-key guard below means the
  // number row is free for the presets.
  useEffect(() => {
    const onKey = (e) => {
      // T290 — nothing while a modal, drawer or help sheet is over the list.
      // The guards below cover typing and modifier keys; they never covered a
      // dialog, so `d` dismissed the row BEHIND the dismiss-reason modal and
      // `1`-`6` fired a preset at it.
      // T290c — the sheet's own CLOSE keys survive the gate, because the sheet
      // being open is one of the things that closes the gate. Disarming the whole
      // hook turned `?` into a key that only ever opened it, and killed the
      // Escape branch at the bottom of this same handler.
      //
      // Narrowly scoped to "the sheet is up": over any OTHER modal `?` stays
      // inert, which is what the T290 test below pins and what the operator
      // should get — a help sheet stacking on top of a dismiss dialog is not an
      // improvement. Neither key touches a recommendation.
      if (showKbHelpOpen && (e.key === '?' || e.key === 'Escape')) {
        const typingHere = ['input', 'textarea', 'select'].includes((e.target?.tagName || '').toLowerCase())
          || e.target?.isContentEditable;
        if (!typingHere && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          setShowKbHelp(e.key === '?' ? (v) => !v : false);
          return;
        }
      }
      if (!enabled) return;
      // Ignore if typing somewhere (search bar, chip input, drawer notes…)
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      // Ignore if a modifier is held (Cmd+K, etc. belong to the palette).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const list = availableRecs;
      const idx = activeRecIndex;
      const cur = idx >= 0 && idx < list.length ? list[idx] : null;

      // Movement
      if (e.key === 'j' || e.key === 'ArrowDown') {
        if (list.length === 0) return;
        e.preventDefault();
        setActiveRecIndex((i) => Math.min(list.length - 1, Math.max(0, i + 1)));
        return;
      }
      if (e.key === 'k' || e.key === 'ArrowUp') {
        if (list.length === 0) return;
        e.preventDefault();
        setActiveRecIndex((i) => i <= 0 ? 0 : i - 1);
        return;
      }

      // From here every shortcut needs an active row
      if (!cur) return;
      if (e.key === 's') {
        e.preventDefault();
        updateRecStatus(cur.id, cur.status === 'shortlisted' ? 'new' : 'shortlisted');
      } else if (e.key === 'a') {
        e.preventDefault();
        updateRecStatus(cur.id, 'applied');
      } else if (e.key === 'd') {
        // T220 — dismiss in ONE key, not one key plus a modal plus a click.
        //
        // Dismiss is the most frequent triage action and was the most expensive
        // one. The reason is left empty on purpose: `isContentfulDismissal`
        // (T196) already treats an empty reason as a fast bulk action and keeps
        // it out of the negative-signal set, so the quick path cannot poison
        // the matcher. Undo lives in the toast, and shift+D still opens the
        // modal when the reason is worth recording.
        e.preventDefault();
        updateRecStatus(cur.id, 'dismissed', { reason: '' });
      } else if (e.key === 'D') {
        e.preventDefault();
        requestDismiss(cur);
      } else if (e.key >= '1' && e.key <= '6') {
        // The six presets from the modal, by position. Five of the six match
        // NON_CONTENT_PATTERNS, so this path loses no training signal either.
        e.preventDefault();
        updateRecStatus(cur.id, 'dismissed', { reason: dismissPresets[Number(e.key) - 1] });
      } else if (e.key === 'e') {
        e.preventDefault();
        explainRec(cur.id);
      } else if (e.key === 'c') {
        e.preventDefault();
        openCoverLetter(cur);
      } else if (e.key === 't') {
        e.preventDefault();
        openTailoredCv(cur);
      } else if (e.key === 'o' || e.key === 'Enter') {
        e.preventDefault();
        setDrawerOpen(true); // T130 — open the drawer for the active rec
      } else if (e.key === '?') {
        e.preventDefault();
        setShowKbHelp((v) => !v);
      } else if (e.key === 'Escape') {
        setShowKbHelp(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // We re-bind whenever availableRecs / activeRecIndex change so the
    // closure captures fresh values without stale refs.
    //
    // T290 — `enabled` is here for the same reason: the listener closes over it,
    // and a stale closure would keep the shortcuts armed for the whole life of
    // the modal that was supposed to disarm them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRecs, activeRecIndex, enabled, showKbHelpOpen]);
}
